import type { FC } from 'react';
import React from 'react';
import { Modal, ModalInner } from '@pflegenavi/web-components';
import type { OnConfirmCallback, OnSkippedCallback } from './ChangeCashForm';
import { ChangeCashForm } from './ChangeCashForm';
import type { ChangeCashMode, ChangeCashSelectorRulesByMode } from './model';
import { useFeatureFlag } from '@pflegenavi/frontend/feature-flags';
import { FeatureFlag, FeatureFlagStatus } from '@pflegenavi/shared/api';

interface InitialValues {
  amount?: number;
  step?: number;
  mode?: ChangeCashMode;
  isStripeTransfer?: boolean;
}

interface ChangeCashModalProps {
  open: boolean;
  setOpen: (open: boolean) => void;
  residentId?: string;
  initialValues?: InitialValues;
  onSkip?: OnSkippedCallback;
  onConfirm?: OnConfirmCallback;
  mandatoryCashLink: boolean;
  /**
   * Per-mode cash-list selector rules. The dropdown is filtered by the rules
   * matching the user's current mode (and per source/target bucket for
   * Transfer). Required: pass `UNRESTRICTED_CASH_LIST_RULES_BY_MODE` to
   * deliberately show all cash lists.
   */
  selectorRulesByMode: ChangeCashSelectorRulesByMode;
  /**
   * Offer the "Beleg herunterladen" checkbox on the confirmation screen for
   * withdrawals producing a ReceiptWithdrawal. Leave off for resident wallets.
   */
  enableWithdrawalReceiptDownload?: boolean;
}

export const ChangeCashModal: FC<ChangeCashModalProps> = ({
  open,
  setOpen,
  residentId,
  initialValues,
  onSkip,
  onConfirm,
  mandatoryCashLink,
  selectorRulesByMode,
  enableWithdrawalReceiptDownload,
}) => {
  const handleClose = (
    _: unknown,
    reason: 'backdropClick' | 'escapeKeyDown'
  ) => {
    if (reason !== 'backdropClick') {
      setOpen(false);
    }
  };

  const accountingPaymentsFeatureFlag =
    useFeatureFlag(FeatureFlag.AccountingPayments) ===
    FeatureFlagStatus.Enabled;

  return (
    <Modal open={open} handleClose={handleClose}>
      <ModalInner>
        <ChangeCashForm
          handleClose={() => setOpen(false)}
          initialValues={initialValues}
          residentId={residentId}
          accountingPaymentsFeatureFlag={accountingPaymentsFeatureFlag}
          onSkip={onSkip}
          onConfirm={onConfirm}
          forceCashListId={undefined}
          mandatoryCashLink={mandatoryCashLink}
          selectorRulesByMode={selectorRulesByMode}
          enableWithdrawalReceiptDownload={enableWithdrawalReceiptDownload}
        />
      </ModalInner>
    </Modal>
  );
};
